import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  JobsFilter,
  selectJobsCurrentActiveView,
  selectJobsCurrentViewFilters,
  selectViewFiltersMap,
  setJobsCurrentViewFilters,
  setViewSpecificFilters
} from '../jobsSlice';
import { getJobViewFilters } from '../utils';
import { TeamView } from 'containers/dispatch/dispatch.model';

/**
 * Custom hook for managing per-view filters
 * Provides utilities to save, load, and manage filters for different team views
 */
export const useViewFilters = () => {
  const dispatch = useAppDispatch();
  const currentActiveView = useAppSelector(selectJobsCurrentActiveView);
  const currentViewFilters = useAppSelector(selectJobsCurrentViewFilters);
  const viewFiltersMap = useAppSelector(selectViewFiltersMap);

  /**
   * Save current filters for the active view
   */
  const saveCurrentViewFilters = useCallback(() => {
    if (currentActiveView) {
      dispatch(
        setViewSpecificFilters({
          viewId: currentActiveView.id.toString(),
          filters: currentViewFilters
        })
      );
    }
  }, [currentActiveView, currentViewFilters, dispatch]);

  /**
   * Load filters for a specific view
   * If no saved filters exist, loads the view's default filters
   */
  const loadViewFilters = useCallback(
    (view: TeamView) => {
      const viewId = view.id.toString();
      const savedFilters = viewFiltersMap[viewId];

      if (savedFilters) {
        // Use saved filters
        dispatch(setJobsCurrentViewFilters(savedFilters));
      } else {
        // Load default filters from view configuration
        const defaultFilters = getJobViewFilters(view.filters);
        dispatch(setJobsCurrentViewFilters(defaultFilters));

        // Save these default filters for future use
        dispatch(
          setViewSpecificFilters({
            viewId,
            filters: defaultFilters
          })
        );
      }
    },
    [viewFiltersMap, dispatch]
  );

  /**
   * Update filters for the current view
   * This both updates the current filters and saves them to the view-specific map
   */
  const updateCurrentViewFilters = useCallback(
    (filters: Record<keyof JobsFilter, string[] | string | number | any | boolean | null>) => {
      dispatch(setJobsCurrentViewFilters(filters));

      // Also save to view-specific map if there's an active view
      if (currentActiveView) {
        dispatch(
          setViewSpecificFilters({
            viewId: currentActiveView.id.toString(),
            filters
          })
        );
      }
    },
    [currentActiveView, dispatch]
  );

  /**
   * Get saved filters for a specific view ID
   */
  const getSavedFiltersForView = useCallback(
    (viewId: string) => {
      return viewFiltersMap[viewId] || null;
    },
    [viewFiltersMap]
  );

  /**
   * Check if a view has any saved filters
   */
  const hasSavedFilters = useCallback(
    (viewId: string) => {
      return !!viewFiltersMap[viewId];
    },
    [viewFiltersMap]
  );

  /**
   * Reset filters for the current view to defaults
   */
  const resetCurrentViewToDefaults = useCallback(() => {
    if (currentActiveView) {
      const defaultFilters = getJobViewFilters(currentActiveView.filters);
      dispatch(setJobsCurrentViewFilters(defaultFilters));

      // Update the saved filters
      dispatch(
        setViewSpecificFilters({
          viewId: currentActiveView.id.toString(),
          filters: defaultFilters
        })
      );
    }
  }, [currentActiveView, dispatch]);

  return {
    // State
    currentActiveView,
    currentViewFilters,
    viewFiltersMap,

    // Actions
    saveCurrentViewFilters,
    loadViewFilters,
    updateCurrentViewFilters,
    getSavedFiltersForView,
    hasSavedFilters,
    resetCurrentViewToDefaults
  };
};
